import {
  CIRCULAR_STATUS_LABEL_PT_BR,
  CircularStatus,
  Permission,
  type SignedCommand,
  type UpdateCircularInput,
} from '@qualicloud/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { SignatureDialog } from '@/components/signature/signature-dialog';
import { PrintableRecord, SignatureManifest } from '@/components/signature/signature-manifest';
import { AcknowledgementCoveragePanel } from '@/components/sgq/acknowledgement-coverage';
import { PeoplePicker } from '@/components/sgq/people-picker';
import { CircularStatusBadge } from '@/components/sgq/sgq-badges';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  IconAlertTriangle,
  IconCheckCircle,
  IconInbox,
  IconInfo,
  IconPrinter,
  Input,
  LoadingRegion,
  NumberInput,
  Skeleton,
  Tabs,
  Textarea,
  useToast,
} from '@/design-system';
import {
  circularsApi,
  queryKeys,
  sgqSignedActInvalidations,
  toUserMessage,
} from '@/lib/api';
import type { CircularDetail } from '@/lib/api/sgq.types';
import { formatRegulatedDateTime, truncateHash } from '@/lib/format/datetime';
import { signedActRecordId } from '@/lib/signed-act-record';
import { useDirectory } from '@/lib/use-directory';
import { useAuth, useOrganizationTimeZone } from '@/providers/auth-provider';
import { PageBody, PageHeader } from '@/shell/app-shell';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CIRCULAR — leitura, publicação e CIÊNCIA ASSINADA
 * sgq.md §D.3, divergência D-CIRC-01
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ABRIR ESTA TELA NÃO REGISTRA CIÊNCIA.
 *
 * No legado a leitura era gravada no simples ato de ABRIR a circular
 * (`controller/circular/circular.php:270`), e `setRead` fazia
 * `UPDATE ... SET lido_em = NOW()` SEM `WHERE lido_em IS NULL`: cada reabertura
 * sobrescrevia o carimbo, de modo que o sistema guardava a ÚLTIMA leitura
 * (BUG-CIR-01). Para evidência de "quando o colaborador tomou ciência", o valor
 * correto é o PRIMEIRO acesso deliberado — e não havia botão "li e concordo"
 * em lugar nenhum.
 *
 * Aqui a ciência é ato assinado (`CIRCULAR_ACKNOWLEDGE`), a primeira é imutável
 * e o painel de cobertura mostra quem leu, quem não leu e o percentual.
 */
export function CircularDetailPage() {
  const { circularId } = useParams({ strict: false }) as { circularId: string };
  const [tab, setTab] = useState('conteudo');
  const [acknowledging, setAcknowledging] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { can, user } = useAuth();
  const timeZone = useOrganizationTimeZone();
  const directory = useDirectory();

  const detailQuery = useQuery({
    queryKey: queryKeys.circulars.detail(circularId),
    queryFn: () => circularsApi.detail(circularId),
  });

  const circular = detailQuery.data;

  const invalidate = async () => {
    await Promise.all(
      sgqSignedActInvalidations().map((key) => queryClient.invalidateQueries({ queryKey: key })),
    );
    await queryClient.invalidateQueries({ queryKey: queryKeys.circulars.detail(circularId) });
  };

  const publish = useMutation({
    mutationFn: (params: {
      readonly additionalRecipientUserIds: string[];
      readonly note: string;
    }) =>
      circularsApi.publish(circularId, {
        additionalRecipientUserIds: params.additionalRecipientUserIds,
        ...(params.note ? { note: params.note } : {}),
      }),
    onSuccess: () => {
      setPublishOpen(false);
      void invalidate();
      toast({
        tone: 'success',
        title: 'Circular publicada',
        description:
          'Os destinatários foram notificados e o texto passa a ser imutável. A partir de agora a cobertura de leitura começa a ser apurada.',
      });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível publicar',
        description: toUserMessage(error),
      }),
  });

  /**
   * Edição do RASCUNHO — BUG-CIR-07.
   *
   * ⚠️ Só os campos ALTERADOS viajam. Omitir os campos de destinatário PRESERVA
   * a lista; mandar array vazio a esvazia. É a mesma guarda anti-wipe da
   * reunião (BUG-REU-07), e o formulário só edita o texto — a distribuição se
   * ajusta na publicação, com `additionalRecipientUserIds`.
   */
  const update = useMutation({
    mutationFn: (input: UpdateCircularInput) => circularsApi.update(circularId, input),
    onSuccess: () => {
      setEditOpen(false);
      void invalidate();
      toast({
        tone: 'success',
        title: 'Rascunho atualizado',
        description:
          'A correção só é possível enquanto a circular é rascunho: publicada, o texto é a evidência da comunicação e o banco recusa alterá-lo.',
      });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível salvar o rascunho',
        description: toUserMessage(error),
      }),
  });

  const discard = useMutation({
    mutationFn: () => circularsApi.discard(circularId),
    onSuccess: async () => {
      setDiscardOpen(false);
      await queryClient.invalidateQueries({ queryKey: queryKeys.circulars.all() });
      toast({
        tone: 'success',
        title: 'Rascunho descartado',
        description:
          'O descarte fica na trilha. O número NÃO volta à sequência: numeração é identidade, e reutilizá-la faria duas circulares carregarem o mesmo código.',
      });
      void navigate({ to: '/circulares' });
    },
    onError: (error) =>
      toast({
        tone: 'error',
        title: 'Não foi possível descartar o rascunho',
        description: toUserMessage(error),
      }),
  });

  const acknowledge = useMutation({
    mutationFn: (command: SignedCommand) =>
      circularsApi.acknowledge(circularId, {
        ...command,
        /*
         * `declaredHostname` preserva o `hostname` do legado — que vinha do DNS
         * reverso do IP do cliente — mas explicitamente marcado como DECLARADO.
         * O navegador não expõe o nome da máquina; o que dá para declarar com
         * honestidade é o host da origem. IP e user-agent efetivos são
         * observados pelo SERVIDOR e não vêm daqui.
         */
        declaredHostname: window.location.hostname,
        acknowledgedActionIds: (circular?.actions ?? []).map((action) => action.id),
        ...(circular?.recordHash ? { expectedRecordHash: circular.recordHash } : {}),
      }),
  });

  if (detailQuery.isPending) {
    return (
      <LoadingRegion loading label="Carregando a circular…">
        <div className="p-5">
          <Skeleton className="h-8 w-96" />
          <Skeleton className="mt-3 h-4 w-[26rem]" />
          <Skeleton className="mt-6 h-72 w-full" />
        </div>
      </LoadingRegion>
    );
  }

  if (!circular) {
    return (
      <PageBody>
        <Card>
          <EmptyState
            icon={<IconInbox size={22} />}
            title="Circular não encontrada"
            description="A circular não existe ou você não é autor nem destinatário. Registros de outras organizações são sempre tratados como inexistentes."
            action={
              <Button asChild variant="primary">
                <Link to="/circulares">Voltar à lista</Link>
              </Button>
            }
          />
        </Card>
      </PageBody>
    );
  }

  const myRecipient = circular.recipients.find((item) => item.userId === user?.id);
  const isAuthor = circular.authorUserId === user?.id;
  const canPublish =
    can(Permission.CIRCULAR_MANAGE) && isAuthor && circular.status === CircularStatus.DRAFT;
  const canAcknowledge =
    circular.status === CircularStatus.PUBLISHED &&
    Boolean(myRecipient) &&
    !myRecipient?.acknowledgedAt;
  /*
   * BUG-CIR-07 fechado. O estado `DRAFT` foi criado com a justificativa de
   * "corrigir erro de digitação antes de virar evidência" e entregava só a
   * publicação — um estado que o usuário alcançava e do qual não conseguia
   * sair. Agora `DRAFT` tem três saídas: editar, descartar e publicar.
   * Publicada, a circular é evidência: o texto é imutável no banco.
   */
  const canEditDraft = canPublish;

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Circulares', to: '/circulares' }, { label: circular.code }]}
        title={`${circular.code} — ${circular.title}`}
        description={`Emitida por ${circular.authorName ?? 'usuário não identificado'}${
          circular.publishedAt
            ? ` · publicada em ${formatRegulatedDateTime(circular.publishedAt, timeZone)}`
            : ' · ainda em rascunho'
        }`}
        actions={
          <>
            {canPublish ? (
              <Button variant="primary" onClick={() => setPublishOpen(true)}>
                Publicar circular
              </Button>
            ) : null}
            {canEditDraft ? (
              <Button variant="secondary" onClick={() => setEditOpen(true)}>
                Editar rascunho
              </Button>
            ) : null}
            {canEditDraft ? (
              <Button
                variant="danger"
                loading={discard.isPending}
                onClick={() => setDiscardOpen(true)}
              >
                Descartar rascunho
              </Button>
            ) : null}
            {canAcknowledge ? (
              <Button
                variant="primary"
                leadingIcon={<IconCheckCircle size={15} />}
                onClick={() => setAcknowledging(true)}
              >
                Declarar ciência
              </Button>
            ) : null}
            <Button
              variant="secondary"
              leadingIcon={<IconPrinter size={15} />}
              onClick={() => window.print()}
            >
              Imprimir
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <CircularStatusBadge status={circular.status} />
          <Badge tone="outline" srPrefix="Destinatários:">
            {circular.recipientCount}{' '}
            {circular.recipientCount === 1 ? 'destinatário' : 'destinatários'}
          </Badge>
          {circular.status === CircularStatus.PUBLISHED ? (
            <Badge
              tone={circular.coveragePercent >= 100 ? 'success' : 'warning'}
              srPrefix="Cobertura de leitura:"
            >
              {circular.coveragePercent.toFixed(1).replace('.', ',')}% de leitura
            </Badge>
          ) : null}
          {myRecipient?.acknowledgedAt ? (
            <Badge tone="success" icon={<IconCheckCircle size={12} />}>
              Você declarou ciência em{' '}
              {formatRegulatedDateTime(myRecipient.acknowledgedAt, timeZone)}
            </Badge>
          ) : null}
        </div>
      </PageHeader>

      <PageBody className="flex flex-col gap-6">
        {canAcknowledge ? (
          <Card className="border-l-4 border-l-brand">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-content">
                  Esta circular aguarda a sua ciência
                </h2>
                <p className="mt-1 max-w-3xl text-sm text-content-muted">
                  Abrir a circular <strong className="font-semibold">não</strong> registra ciência.
                  A confirmação é um ato deliberado e assinado eletronicamente, e a primeira é a
                  que vale — ela não pode ser substituída depois.
                  {myRecipient?.dueAt
                    ? ` O prazo é ${formatRegulatedDateTime(myRecipient.dueAt, timeZone)}.`
                    : ''}
                </p>
              </div>
              <Button
                variant="primary"
                leadingIcon={<IconCheckCircle size={15} />}
                onClick={() => setAcknowledging(true)}
              >
                Declarar ciência
              </Button>
            </div>
          </Card>
        ) : null}

        {circular.status === CircularStatus.DRAFT ? (
          <p
            role="status"
            className="flex items-start gap-2 rounded-md border border-info bg-info-subtle px-3 py-2.5 text-sm text-info-content"
          >
            <IconInfo size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              Esta circular está em <strong className="font-semibold">rascunho</strong>: ninguém foi
              notificado e nenhuma ciência é cobrada. O texto ainda pode ser corrigido. Depois de
              publicada, a correção passa a ser uma nova circular.
            </span>
          </p>
        ) : null}

        <Tabs
          ariaLabel="Seções da circular"
          value={tab}
          onValueChange={setTab}
          tabs={[
            {
              value: 'conteudo',
              label: 'Comunicado',
              content: (
                <div className="grid gap-5 lg:grid-cols-[2fr_1fr]">
                  <Card>
                    {/*
                      Nível 2: esta tela não tem heading fixo entre o h1 do
                      cabeçalho e os cartões — o único h2 anterior é o aviso de
                      ciência pendente, que só existe para o destinatário sem
                      ciência. Fixar 3 aqui abriria buraco na cadeia para todo
                      mundo (autor, rascunho, quem já deu ciência).
                    */}
                    <CardHeader headingLevel={2} title={circular.title} />
                    <p className="mt-3 whitespace-pre-line text-sm text-content">
                      {circular.body}
                    </p>
                  </Card>

                  <Card>
                    <CardHeader headingLevel={2} title="Dados" />
                    <dl className="mt-3 grid grid-cols-[9rem_1fr] gap-x-4 gap-y-2 text-sm">
                      <dt className="text-content-subtle">Número</dt>
                      <dd className="font-mono text-content">{circular.code}</dd>
                      <dt className="text-content-subtle">Situação</dt>
                      <dd className="text-content">
                        {CIRCULAR_STATUS_LABEL_PT_BR[circular.status]}
                      </dd>
                      <dt className="text-content-subtle">Autor</dt>
                      <dd className="text-content">{circular.authorName ?? '—'}</dd>
                      <dt className="text-content-subtle">Prazo de ciência</dt>
                      <dd className="text-content qc-tabular">
                        {circular.acknowledgementDueDays} dias após a publicação
                      </dd>
                    </dl>

                    {circular.actions.length > 0 ? (
                      <div className="mt-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
                          Ações a tomar
                        </h3>
                        <ul className="mt-2 flex flex-col gap-1.5">
                          {circular.actions.map((action) => (
                            <li
                              key={action.id}
                              className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-sm"
                            >
                              <p className="font-medium text-content">{action.name}</p>
                              {action.description ? (
                                <p className="mt-0.5 text-xs text-content-muted">
                                  {action.description}
                                </p>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {circular.items.length > 0 ? (
                      <div className="mt-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
                          Itens relacionados
                        </h3>
                        <ul className="mt-2 flex flex-col gap-1.5">
                          {circular.items.map((item) => (
                            <li key={item.id} className="text-sm text-content">
                              <Badge tone="outline" size="sm" className="mr-2">
                                {ITEM_KIND_LABEL[item.kind]}
                              </Badge>
                              <span className="font-mono">{item.code}</span>
                              {item.description ? ` — ${item.description}` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </Card>
                </div>
              ),
            },
            {
              value: 'cobertura',
              label: 'Cobertura de leitura',
              count: circular.recipientCount,
              content: (
                <AcknowledgementCoveragePanel
                  recipients={circular.recipients}
                  timeZone={timeZone}
                  serverCoveragePercent={circular.coveragePercent}
                />
              ),
            },
          ]}
        />

        <SignatureManifest
          entries={[...circular.signatures]}
          recordLabelFallback={`${circular.code} — ${circular.title}`}
          headingLevel={2}
        />

        <PrintableRecord
          title={`${circular.code} — ${circular.title}`}
          subtitle={`Circular interna · ${CIRCULAR_STATUS_LABEL_PT_BR[circular.status]}`}
          metadata={[
            { label: 'Autor', value: circular.authorName ?? '—' },
            {
              label: 'Publicada em',
              value: circular.publishedAt
                ? formatRegulatedDateTime(circular.publishedAt, timeZone)
                : 'Não publicada',
            },
            { label: 'Destinatários', value: String(circular.recipientCount) },
            {
              label: 'Ciências registradas',
              value: `${circular.acknowledgedCount} (${circular.coveragePercent
                .toFixed(1)
                .replace('.', ',')}%)`,
            },
            { label: 'Comunicado', value: circular.body },
            { label: 'Impressão em', value: formatRegulatedDateTime(new Date(), timeZone) },
          ]}
          entries={[...circular.signatures]}
        />
      </PageBody>

      {/* ── Edição e descarte do rascunho (BUG-CIR-07) ──────────────────── */}
      <CircularDraftDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        circular={circular}
        busy={update.isPending}
        onConfirm={(input) => update.mutate(input)}
      />

      <Dialog
        open={discardOpen}
        onOpenChange={(next) => {
          if (discard.isPending) return;
          setDiscardOpen(next);
        }}
        title="Descartar o rascunho"
        description={`${circular.code} nunca foi publicada: nenhum destinatário foi notificado e nenhuma ciência foi registrada, portanto nada de evidência é apagado. O NÚMERO não retorna à sequência.`}
        dismissible={!discard.isPending}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={discard.isPending}
              onClick={() => setDiscardOpen(false)}
            >
              Voltar
            </Button>
            <Button
              variant="danger"
              loading={discard.isPending}
              onClick={() => discard.mutate()}
            >
              Descartar definitivamente
            </Button>
          </>
        }
      >
        <p className="text-sm text-content-subtle">
          O descarte é registrado na trilha de auditoria com o número e o título, como qualquer
          outro ato.
        </p>
      </Dialog>

      {/* ── Publicação ──────────────────────────────────────────────────── */}
      <PublishDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        busy={publish.isPending}
        recipientCount={circular.recipientCount}
        users={directory.subjects
          .filter((subject) => !circular.recipients.some((item) => item.userId === subject.userId))
          .map((subject) => ({
            id: subject.userId,
            name: subject.displayName,
            hint: subject.jobTitle,
          }))}
        onConfirm={(params) => publish.mutate(params)}
      />

      {/* ── Ciência assinada ────────────────────────────────────────────── */}
      {acknowledging ? (
        <SignatureDialog
          open
          onOpenChange={(next) => {
            if (!next) setAcknowledging(false);
          }}
          act="CIRCULAR_ACKNOWLEDGE"
          recordId={
            signedActRecordId('CIRCULAR_ACKNOWLEDGE', {
              documentVersionId: null,
              acknowledgementId: null,
              circularId: circular.id,
            }) ?? ''
          }
          recordLabel={`${circular.code} — ${circular.title}`}
          recordDetails={[
            { label: 'Autor', value: circular.authorName ?? '—' },
            {
              label: 'Publicada em',
              value: circular.publishedAt
                ? formatRegulatedDateTime(circular.publishedAt, timeZone)
                : '—',
            },
            {
              label: 'Prazo da sua ciência',
              value: myRecipient?.dueAt
                ? formatRegulatedDateTime(myRecipient.dueAt, timeZone)
                : 'Sem prazo definido',
            },
            { label: 'Impressão digital do registro', value: truncateHash(circular.recordHash) },
          ]}
          confirmLabel="Declarar que li e tomei ciência"
          onSign={async (command) => {
            await acknowledge.mutateAsync(command);
          }}
          onSigned={() => {
            setAcknowledging(false);
            void invalidate();
            toast({
              tone: 'success',
              title: 'Ciência registrada',
              description:
                'A sua confirmação foi gravada junto com a assinatura eletrônica. Esta é a primeira ciência e ela é definitiva — reabrir a circular não altera o carimbo.',
            });
          }}
        />
      ) : null}
    </>
  );
}

const ITEM_KIND_LABEL: Record<'PRODUCT' | 'MATERIAL' | 'FREE_TEXT', string> = {
  PRODUCT: 'Produto',
  MATERIAL: 'Matéria-prima',
  FREE_TEXT: 'Item livre',
};

/**
 * EDIÇÃO DO RASCUNHO — BUG-CIR-07.
 *
 * O legado não tinha `editCircular` nem `deleteCircular`: a circular nascia
 * publicada, o e-mail saía dentro do `add`, e um erro de digitação no título era
 * permanente. O produto novo criou o estado `DRAFT` invocando exatamente essa
 * correção — e entregava só a publicação.
 *
 * Edita o TEXTO e o prazo. A distribuição continua sendo ajustada na publicação
 * (`additionalRecipientUserIds`), e por isso este formulário NÃO envia os campos
 * de destinatário: omiti-los preserva a lista, e mandá-los vazios a esvaziaria.
 */
function CircularDraftDialog({
  open,
  onOpenChange,
  circular,
  busy,
  onConfirm,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly circular: CircularDetail;
  readonly busy: boolean;
  readonly onConfirm: (input: UpdateCircularInput) => void;
}) {
  const [title, setTitle] = useState(circular.title);
  const [body, setBody] = useState(circular.body);
  const [dueDays, setDueDays] = useState(String(circular.acknowledgementDueDays));

  const reset = () => {
    setTitle(circular.title);
    setBody(circular.body);
    setDueDays(String(circular.acknowledgementDueDays));
  };

  // Os mesmos limites do contrato e do CHECK de banco (§D.1, `validateForm` de
  // `circular.php:563-568`).
  const titleInvalid = title.trim().length < 10 || title.trim().length > 255;
  const bodyInvalid = body.trim().length < 10 || body.trim().length > 3000;

  const submit = () => {
    const input: Record<string, unknown> = {};
    if (title !== circular.title) input.title = title;
    if (body !== circular.body) input.body = body;
    if (Number(dueDays) !== circular.acknowledgementDueDays) {
      input.acknowledgementDueDays = Number(dueDays);
    }
    onConfirm(input as UpdateCircularInput);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        if (next) reset();
        onOpenChange(next);
      }}
      size="lg"
      title="Editar o rascunho"
      description="Só o rascunho é editável. Depois de publicada, a circular é a evidência de que a comunicação aconteceu, com data e destinatários — alterar o texto faria a ciência já registrada se referir a algo que ninguém leu."
      dismissible={!busy}
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={titleInvalid || bodyInvalid}
            onClick={submit}
          >
            Salvar rascunho
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Título"
          required
          error={
            title.length > 0 && titleInvalid
              ? 'O título deve ter entre 10 e 255 caracteres.'
              : undefined
          }
        >
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={255}
            disabled={busy}
          />
        </Field>
        <Field
          label="Comunicado"
          required
          error={
            body.length > 0 && bodyInvalid
              ? 'O comunicado deve ter entre 10 e 3000 caracteres.'
              : undefined
          }
        >
          <Textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={10}
            maxLength={3000}
            showCounter
            disabled={busy}
          />
        </Field>
        <Field
          label="Prazo para ciência (dias)"
          description="Contado a partir da publicação. Alimenta a cobrança automática de circular sem ciência."
        >
          <NumberInput
            value={dueDays}
            onChange={(event) => setDueDays(event.target.value)}
            min={1}
            max={365}
            className="w-28"
            disabled={busy}
          />
        </Field>
      </div>
    </Dialog>
  );
}

function PublishDialog({
  open,
  onOpenChange,
  busy,
  recipientCount,
  users,
  onConfirm,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly busy: boolean;
  readonly recipientCount: number;
  readonly users: ReadonlyArray<{
    readonly id: string;
    readonly name: string;
    readonly hint?: string | undefined;
  }>;
  readonly onConfirm: (params: {
    readonly additionalRecipientUserIds: string[];
    readonly note: string;
  }) => void;
}) {
  const [additional, setAdditional] = useState<string[]>([]);
  const [note, setNote] = useState('');

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && busy) return;
        onOpenChange(next);
      }}
      dismissible={!busy}
      size="lg"
      title="Publicar a circular"
      description="A publicação notifica os destinatários e torna o texto imutável. Só o autor publica."
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() => onConfirm({ additionalRecipientUserIds: additional, note: note.trim() })}
          >
            Publicar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p
          role="alert"
          className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5 text-sm text-warning-content"
        >
          <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>
            Depois de publicada, a circular <strong className="font-semibold">não pode ser
            editada</strong>. Uma correção exige emitir uma nova circular, e a antiga permanece no
            registro. Revise o texto antes de continuar.
          </span>
        </p>

        <p className="text-sm text-content">
          Serão notificados{' '}
          <strong className="font-semibold">
            {recipientCount + additional.length}{' '}
            {recipientCount + additional.length === 1 ? 'destinatário' : 'destinatários'}
          </strong>
          .
        </p>

        <PeoplePicker
          noun="destinatários adicionais"
          userFieldLabel="Acrescentar destinatário na publicação"
          userFieldDescription="Opcional. Estes colaboradores entram junto com os já cadastrados."
          users={users}
          userIds={additional}
          onUserIdsChange={setAdditional}
          disabled={busy}
          emptyHint="Nenhum destinatário adicional. A circular vai para os já cadastrados."
        />

        <Field
          label="Observação da publicação"
          description="Opcional. Fica registrada na trilha de auditoria junto do ato de publicar."
        >
          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            maxLength={2000}
            showCounter
          />
        </Field>
      </div>
    </Dialog>
  );
}
